import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasConsent, getConsent, acceptAll, rejectAll, setConsent, COOKIE_PREFERENCES_EVENT } from '../utils/cookieConsent';
import './CookieConsent.scss';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const openPreferences = useCallback(() => {
    const consent = getConsent();
    setAnalyticsEnabled(consent?.analytics ?? false);
    setShowPreferences(true);
  }, []);

  useEffect(() => {
    setVisible(!hasConsent());
  }, []);

  useEffect(() => {
    const handler = () => openPreferences();
    window.addEventListener(COOKIE_PREFERENCES_EVENT, handler);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, handler);
  }, [openPreferences]);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleManagePreferences = () => {
    openPreferences();
  };

  const handleSavePreferences = () => {
    setConsent(true, analyticsEnabled);
    setVisible(false);
    setShowPreferences(false);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
  };

  if (!visible && !showPreferences) return null;

  return (
    <>
      {visible && (
      <div className="cookie-consent" role="dialog" aria-label={t('cookieConsent.ariaLabel')}>
        <div className="cookie-consent__content">
          <h3 className="cookie-consent__title">{t('cookieConsent.title')}</h3>
          <p className="cookie-consent__description">{t('cookieConsent.description')}</p>
          <Link to="/terms#cookies" className="cookie-consent__link">
            {t('cookieConsent.privacyLink')}
          </Link>
          <div className="cookie-consent__buttons">
            <button
              type="button"
              className="cookie-consent__btn cookie-consent__btn--primary"
              onClick={handleAcceptAll}
            >
              {t('cookieConsent.acceptAll')}
            </button>
            <button
              type="button"
              className="cookie-consent__btn cookie-consent__btn--secondary"
              onClick={handleRejectAll}
            >
              {t('cookieConsent.rejectAll')}
            </button>
            <button
              type="button"
              className="cookie-consent__btn cookie-consent__btn--tertiary"
              onClick={handleManagePreferences}
            >
              {t('cookieConsent.managePreferences')}
            </button>
          </div>
        </div>
      </div>
      )}

      {showPreferences && (
        <div
          className="cookie-consent-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
        >
          <div className="cookie-consent-modal">
            <h2 id="cookie-preferences-title" className="cookie-consent-modal__title">
              {t('cookieConsent.preferences.title')}
            </h2>
            <p className="cookie-consent-modal__desc">
              {t('cookieConsent.preferences.description')}
            </p>

            <div className="cookie-consent-modal__section">
              <div className="cookie-consent-modal__toggle-row">
                <div>
                  <strong>{t('cookieConsent.preferences.necessaryTitle')}</strong>
                  <p>{t('cookieConsent.preferences.necessaryDesc')}</p>
                </div>
                <span className="cookie-consent-modal__badge">{t('cookieConsent.preferences.alwaysOn')}</span>
              </div>
            </div>

            <div className="cookie-consent-modal__section">
              <div className="cookie-consent-modal__toggle-row">
                <div>
                  <strong>{t('cookieConsent.preferences.analyticsTitle')}</strong>
                  <p>{t('cookieConsent.preferences.analyticsDesc')}</p>
                </div>
                <label className="cookie-consent-modal__toggle">
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  />
                  <span className="cookie-consent-modal__slider" />
                </label>
              </div>
            </div>

            <Link to="/terms#cookies" className="cookie-consent-modal__link">
              {t('cookieConsent.privacyLink')}
            </Link>

            <div className="cookie-consent-modal__buttons">
              <button
                type="button"
                className="cookie-consent__btn cookie-consent__btn--primary"
                onClick={handleSavePreferences}
              >
                {t('cookieConsent.preferences.save')}
              </button>
              <button
                type="button"
                className="cookie-consent__btn cookie-consent__btn--secondary"
                onClick={handleClosePreferences}
              >
                {t('cookieConsent.preferences.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
